/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useHistory} from "react-router";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {consoleLog} from "utils/utility";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import BookingLoginInput from "pages/Front/Booking/components/BookingLoginInput";
import BookingLoginErrors from "pages/Front/Booking/BookingLogin/components/BookingLoginErrors";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import {toast} from "react-toastify";
import SellerAction from "store/seller/SellerAction";
import {Link} from "react-router-dom";

const REGEX_EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

interface BookingSellerLoginWithPasswordProps {
}

const BookingSellerLoginWithPassword = (props: BookingSellerLoginWithPasswordProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const LoginSchema = yup.object().shape({
        email: yup
            .string()
            .required(t("Email is required field."))
            .test("email", t("Invalid email format"), value => {
                return REGEX_EMAIL.test(value);
            }),
        password: yup
            .string()
            .required(t("Password is required field."))
    });
    const defaultValues = {
        email: "",
        password: "",
    };
    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm({
        resolver: yupResolver<any>(LoginSchema),
        defaultValues: defaultValues,
    });

    const onSubmitError = (data: FieldErrors) => {
        consoleLog("onSubmitError", data);
    };

    const onSubmitHandler = data => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        dispatch(SellerAction.fetchGetSellerLoginWithPassword(data.email, data.password))
            .then(
                onResolve => {
                    dispatch(BookingLoginAction.setReservations(onResolve?.reservations ? onResolve.reservations : []));
                    dispatch(BookingLoginAction.setUser(onResolve));
                    history.replace("/booking/seller/reservations");
                },
                onReject => {
                    if (onReject === "password_invalid") {
                        toast.error(t("Login password invalid"));
                    }
                },
            )
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className={"BookingSellerLoginWithPassword w-full flex justify-center items-center"}>
            <CardComponent className={"w-full max-w-md"}>
                <form
                    autoComplete={"off"}
                    className={"BookingSellerLoginWithPasswordForm w-full"}
                    onSubmit={handleSubmit(onSubmitHandler, onSubmitError)}
                >
                    <div className={"my-2 text-center space-y-2"}>
                        <label className="text-base font-bold text-gray-900">{t("Login for seller")}</label>
                    </div>

                    <div className={"space-y-2"}>
                        <BookingLoginInput
                            className={"w-full pl-10"}
                            errors={errors}
                            label={t("Email")}
                            name={"email"}
                            placeholder={"mail@example.com"}
                            register={register}
                            required={true}
                            showIcon={true}
                            type={"email"}
                        />
                        <BookingLoginInput
                            className={"w-full pl-10"}
                            errors={errors}
                            label={t("Password")}
                            name={"password"}
                            placeholder={""}
                            register={register}
                            required={true}
                            showIcon={true}
                            type={"password"}
                        />
                    </div>

                    {errors && <BookingLoginErrors errors={errors} />}

                    <div className={"sm:col-span-6 mt-4"}>
                        <div className={"flex justify-center"}>
                            <ButtonSubmit
                                buttonType={"cta"}
                                className={"rounded-md"}
                                disabled={isSubmitting}
                                icon={"check"}
                                isLoading={isSubmitting}
                                label={t("Login")}
                                onClick={() => handleSubmit(onSubmitHandler, onSubmitError)}
                            />
                        </div>
                    </div>

                    <div className={"sm:col-span-6 mt-4 text-center"}>
                        <Link to={"/booking/seller/forgotten-password"}>
                            <span>{t("Forgot password")}?</span>
                        </Link>
                    </div>

                </form>
            </CardComponent>
        </div>
    );
};

export default BookingSellerLoginWithPassword;